import { notify } from "@/components/common/Notification";
import {
  clearLocalStorage,
  getAccessToken,
  getRefreshToken,
  setLocalAccessToken,
  setLocalRefreshToken,
} from "@/lib/storeManager";
import { hidenMessageAPI } from "@/utils/constant/hidenMessageAPI";
import {
  getRuntimeConfig,
  isRuntimeConfigLoaded,
} from "@/config/runtimeConfig";
import axios from "axios";

/** Single API origin for this app (paths like `/auth/`, `/social-login-config/` are appended here). */
function resolveApiBaseUrl(): string {
  if (isRuntimeConfigLoaded()) {
    return getRuntimeConfig().apiUrl;
  }
  return (
    import.meta.env.VITE_JAIN_ELIBRARY_API_URL ?? "http://localhost:8000/api"
  );
}

export const axiosConfig = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
});

axiosConfig.interceptors.request.use(config => {
  const token = getAccessToken();

  // Don't add Authorization for refresh or OTP (signup) - user not logged in for signup
  const skipAuth = ["/auth/token-refresh/", "/auth/verify-email/"].some(path =>
    config.url?.includes(path)
  );
  if (token && config.headers && !skipAuth) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

let isRefreshing = false;
let failedQueue: {
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

axiosConfig.interceptors.response.use(
  response => {
    const apiStatus = response.status;
    if (
      hidenMessageAPI.some(path => (response.config.url ?? "").includes(path))
    ) {
      return response;
    }
    if (
      (apiStatus === 200 || apiStatus === 201 || apiStatus === 204) &&
      response.data?.success
    ) {
      const message = response.data?.success?.message ?? response.data.message;
      if (message) {
        notify.success(message);
      }
    }
    return response;
  },
  async error => {
    const originalRequest = error.config;
    const requestUrl = originalRequest?.url ?? "";

    // For auth endpoints (login/signup), show error toast and reject
    const isAuthEndpoint = ["/auth/", "/forgot-password"].some(path =>
      requestUrl.includes(path)
    );

    if (
      error.response?.status === 400 ||
      error.response?.status === 404 ||
      error.response?.status === 417
    ) {
      // Don't show toast for optional APIs (e.g. user preferences not yet on backend)
      const isSilentUrl = hidenMessageAPI.some(path =>
        requestUrl.includes(path)
      );
      if (!isSilentUrl) {
        const errorMessage =
          error.response?.data?.message ??
          error.response?.data?.error?.message ??
          error.response?.data?.error ??
          error.response?.data?.email?.[0] ??
          error.response?.data?.non_field_errors?.[0] ??
          "An error occurred";
        notify.error(errorMessage);
      }
      return Promise.reject(error);
    }

    // For login/signup 401 errors, show error and reject (don't try to refresh token)
    if (error.response?.status === 401 && isAuthEndpoint) {
      const errorMessage =
        error.response.data.message ??
        error.response.data.error?.message ??
        error.response.data.error ??
        error.response.data.detail ?? // Some APIs use 'detail' field
        "Invalid credentials. Please try again.";
      notify.error(errorMessage);
      return Promise.reject(error);
    }

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      getRefreshToken()
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers["Authorization"] = `Bearer ${token}`;
          return axiosConfig(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const response = await refreshToken();
        const newAccessToken = response.access;
        setLocalAccessToken(newAccessToken);
        if (response.refresh) {
          setLocalRefreshToken(response.refresh);
        }
        axiosConfig.defaults.headers.common["Authorization"] =
          `Bearer ${newAccessToken}`;
        processQueue(null, newAccessToken);
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        return axiosConfig(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearLocalStorage();
        window.location.reload();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    } else {
      const isSilentUrl = hidenMessageAPI.some(path =>
        requestUrl.includes(path)
      );
      if (!isSilentUrl) {
        const msg =
          error.response?.data?.error?.message ??
          error.response?.data?.message ??
          error.response?.data?.error ??
          error.response?.data?.detail ??
          "An error occurred";
        notify.error(msg);
      }
      return Promise.reject(error);
    }
  }
);

const refreshToken = async () => {
  const response = await axios.post(
    `${axiosConfig.defaults.baseURL ?? resolveApiBaseUrl()}/auth/token-refresh/`,
    new URLSearchParams({
      refresh: getRefreshToken() ?? "",
    }),
    {
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    }
  );
  return response.data;
};
